package com.ijoin.ui.screens

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.CenterAlignedTopAppBar
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.ijoin.data.repository.AuthRepository
import com.ijoin.i18n.AppLanguage
import com.ijoin.i18n.LocalLocaleManager
import com.ijoin.i18n.SupportedLanguages
import com.ijoin.i18n.strings
import com.ijoin.ui.profile.EditableUserFields
import com.ijoin.ui.profile.ProfileMessage
import com.ijoin.ui.profile.ProfileUiState
import com.ijoin.ui.profile.ProfileViewModel
import kotlinx.coroutines.launch
import com.ijoin.getPlatform
import com.ijoin.platform.isWebMobile
import com.ijoin.ui.components.MainBottomNavigationBar
import com.ijoin.ui.components.SideNavigationRail
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.AlertDialog
import com.ijoin.platform.rememberMediaPicker
import com.ijoin.ui.util.resolveMediaUrl
import com.ijoin.ui.components.RefreshableContainer
import com.ijoin.ui.components.rememberPullToRefreshState
import com.ijoin.analytics.AnalyticsTracker
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventDangerButton
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSectionCard
import com.ijoin.ui.design.EventSelectableCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.design.eventOutlinedFieldColors
import com.ijoin.data.repository.LegalRepository
import com.ijoin.ui.components.AiConsentDialog

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProfileScreen(
    authRepository: AuthRepository,
    onBackClick: () -> Unit,
    onLogout: () -> Unit,
    onOpenHome: () -> Unit,
    onOpenExplore: () -> Unit,
    onCreateEventClick: () -> Unit,
    onOpenTickets: () -> Unit,
    onOpenProfile: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: ProfileViewModel = remember(authRepository) { ProfileViewModel(authRepository) }
) {
    val uiState by viewModel.uiState.collectAsState()
    val strings = strings()
    val localeManager = LocalLocaleManager.current
    val availableLanguages = remember { SupportedLanguages.all }
    var syncedLanguageCode by remember { mutableStateOf<String?>(null) }
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    var isLoggingOut by remember { mutableStateOf(false) }
    var showDeleteDialog by remember { mutableStateOf(false) }
    val avatarPicker = rememberMediaPicker()
    val handleLanguageSelected: (AppLanguage) -> Unit = { language ->
        viewModel.updateEditableUser { it.copy(language = language) }
        localeManager.setLanguage(language)
    }

    LaunchedEffect(uiState.user?.language) {
        val preferred = uiState.user?.language
        if (preferred != null && preferred != syncedLanguageCode) {
            localeManager.setLanguage(SupportedLanguages.fromCode(preferred))
            syncedLanguageCode = preferred
        }
    }

    LaunchedEffect(avatarPicker.selectedFiles) {
        val dataUrl = avatarPicker.selectedFiles.firstOrNull { it.startsWith("data:image") }
        if (dataUrl != null) {
            viewModel.setAvatarDataUrl(dataUrl)
        }
    }

    LaunchedEffect(uiState.errorMessage) {
        val message = uiState.errorMessage ?: return@LaunchedEffect
        snackbarHostState.showSnackbar(message)
        viewModel.clearError()
    }

    LaunchedEffect(uiState.message) {
        when (uiState.message) {
            ProfileMessage.Saved -> {
                snackbarHostState.showSnackbar(strings.profileSavedMessage)
                val userId = uiState.user?.id?.toString()
                AnalyticsTracker.trackCompleteRegistration(userId)
            }
            ProfileMessage.Deleted -> {
                snackbarHostState.showSnackbar(strings.profileDeletedMessage)
                onLogout()
            }
            null -> Unit
        }
        if (uiState.message != null) {
            viewModel.clearMessage()
        }
    }

    if (showDeleteDialog) {
        AlertDialog(
            onDismissRequest = { if (!uiState.isDeleting) showDeleteDialog = false },
            title = { Text(strings.deleteAccountTitle) },
            text = { Text(strings.deleteAccountConfirmMessage) },
            confirmButton = {
                EventTextAction(
                    onClick = {
                        if (uiState.isDeleting) return@EventTextAction
                        viewModel.deleteAccount()
                        showDeleteDialog = false
                    },
                    enabled = !uiState.isDeleting,
                    isLoading = uiState.isDeleting,
                    text = strings.deleteAccountAction,
                    contentColor = EventCardAlert
                )
            },
            dismissButton = {
                EventTextAction(
                    onClick = { showDeleteDialog = false },
                    enabled = !uiState.isDeleting,
                    text = strings.cancel
                )
            }
        )
    }

    val platform = getPlatform()
    val isWeb = platform.isWeb
    val isDesktopWeb = isWeb && !isWebMobile()

    if (isDesktopWeb) {
        Row(modifier = Modifier.fillMaxSize()) {
            SideNavigationRail(
                selectedTab = "profile",
                onTabSelected = { tab ->
                    when (tab) {
                        "home" -> onOpenHome()
                        "explore" -> onOpenExplore()
                        "create" -> onCreateEventClick()
                        "tickets" -> onOpenTickets()
                        "profile" -> onOpenProfile()
                    }
                },
                showAppStoreLinks = true
            )

            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(eventBackgroundBrush()),
                contentAlignment = Alignment.TopCenter
            ) {
                Scaffold(
                    modifier = Modifier.fillMaxWidth(0.6f),
                    containerColor = EventCardBackground,
                    topBar = {
                        CenterAlignedTopAppBar(
                            title = {
                                Text(
                                    text = strings.profile,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis
                                )
                            },
                            navigationIcon = {
                                IconButton(onClick = onBackClick) {
                                    Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                                }
                            },
                            colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                                containerColor = EventCardBackground,
                                titleContentColor = EventCardAccentDark,
                                navigationIconContentColor = EventCardAccentDark
                            )
                        )
                    },
                    snackbarHost = { SnackbarHost(snackbarHostState) }
                ) { innerPadding ->
                    Box(
                        modifier = Modifier
                            .padding(innerPadding)
                            .fillMaxSize()
                    ) {
                when {
                    uiState.isLoading -> {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            CircularProgressIndicator()
                        }
                    }
                    uiState.user != null -> {
                        val scrollState = rememberScrollState()
                        val pullState = rememberPullToRefreshState(
                            refreshing = uiState.isLoading,
                            onRefresh = viewModel::refreshProfile,
                            canRefresh = { scrollState.value == 0 }
                        )
                        RefreshableContainer(
                            modifier = Modifier.fillMaxSize(),
                            state = pullState,
                            enabled = true
                        ) { pullModifier ->
                            ProfileContent(
                                uiState = uiState,
                                selectedLanguage = uiState.editableUser.language,
                                availableLanguages = availableLanguages,
                                onLanguageSelected = handleLanguageSelected,
                                onStartEditing = viewModel::startEditing,
                                onCancelEditing = viewModel::cancelEditing,
                                onFieldChange = { transform -> viewModel.updateEditableUser(transform) },
                                onSave = viewModel::saveProfileChanges,
                                onLogoutClick = {
                                    if (isLoggingOut || uiState.isDeleting) return@ProfileContent
                                    scope.launch {
                                        isLoggingOut = true
                                        val result = authRepository.logout()
                                        result.onFailure { error ->
                                            snackbarHostState.showSnackbar(
                                                error.message ?: strings.logoutError
                                            )
                                        }
                                        isLoggingOut = false
                                        onLogout()
                                    }
                                },
                                onPickAvatar = avatarPicker.pickFiles,
                                onDeleteAccountClick = { showDeleteDialog = true },
                                isLoggingOut = isLoggingOut,
                                isDeleting = uiState.isDeleting,
                                modifier = pullModifier,
                                scrollState = scrollState
                            )
                        }
                    }
                            else -> {
                                Column(
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(24.dp),
                                    verticalArrangement = Arrangement.Center,
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(strings.profileLoadError)
                                    Spacer(modifier = Modifier.height(8.dp))
                                    EventOutlineButton(
                                        text = strings.retry,
                                        onClick = viewModel::refreshProfile
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    } else {
        Scaffold(
            modifier = modifier,
            containerColor = EventCardBackground,
            topBar = {
                CenterAlignedTopAppBar(
                    title = {
                        Text(
                            text = strings.profile,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    },
                    navigationIcon = {
                        IconButton(onClick = onBackClick) {
                            Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                        }
                    },
                    colors = TopAppBarDefaults.centerAlignedTopAppBarColors(
                        containerColor = EventCardBackground,
                        titleContentColor = EventCardAccentDark,
                        navigationIconContentColor = EventCardAccentDark
                    )
                )
            },
            snackbarHost = { SnackbarHost(snackbarHostState) },
            bottomBar = {
                MainBottomNavigationBar(
                    selectedTab = "profile",
                    onTabSelected = { tab ->
                        when (tab) {
                            "home" -> onOpenHome()
                            "explore" -> onOpenExplore()
                            "create" -> onCreateEventClick()
                            "tickets" -> onOpenTickets()
                            "profile" -> onOpenProfile()
                        }
                    }
                )
            }
        ) { innerPadding ->
            Box(
                modifier = Modifier
                    .padding(innerPadding)
                    .fillMaxSize()
                    .background(eventBackgroundBrush())
            ) {
                when {
                    uiState.isLoading -> {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            CircularProgressIndicator()
                        }
                    }
                    uiState.user != null -> {
                        ProfileContent(
                            uiState = uiState,
                            selectedLanguage = uiState.editableUser.language,
                            availableLanguages = availableLanguages,
                            onLanguageSelected = handleLanguageSelected,
                            onStartEditing = viewModel::startEditing,
                            onCancelEditing = viewModel::cancelEditing,
                            onFieldChange = { transform -> viewModel.updateEditableUser(transform) },
                            onSave = viewModel::saveProfileChanges,
                            onLogoutClick = {
                                if (isLoggingOut || uiState.isDeleting) return@ProfileContent
                                scope.launch {
                                    isLoggingOut = true
                                    val result = authRepository.logout()
                                    result.onFailure { error ->
                                        snackbarHostState.showSnackbar(
                                            error.message ?: strings.logoutError
                                        )
                                    }
                                    isLoggingOut = false
                                    onLogout()
                                }
                            },
                            onPickAvatar = avatarPicker.pickFiles,
                            onDeleteAccountClick = { showDeleteDialog = true },
                            isLoggingOut = isLoggingOut,
                            isDeleting = uiState.isDeleting,
                            modifier = Modifier.fillMaxSize(),
                            scrollState = rememberScrollState()
                        )
                    }
                    else -> {
                        Column(
                            modifier = Modifier
                                .fillMaxSize()
                                .padding(24.dp),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(strings.profileLoadError)
                            Spacer(modifier = Modifier.height(8.dp))
                            EventOutlineButton(
                                text = strings.retry,
                                onClick = viewModel::refreshProfile
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ProfileContent(
    uiState: ProfileUiState,
    selectedLanguage: AppLanguage,
    availableLanguages: List<AppLanguage>,
    onLanguageSelected: (AppLanguage) -> Unit,
    onStartEditing: () -> Unit,
    onCancelEditing: () -> Unit,
    onFieldChange: ((EditableUserFields) -> EditableUserFields) -> Unit,
    onSave: () -> Unit,
    onLogoutClick: () -> Unit,
    onDeleteAccountClick: () -> Unit,
    onPickAvatar: () -> Unit,
    isLoggingOut: Boolean,
    isDeleting: Boolean,
    modifier: Modifier = Modifier,
    scrollState: ScrollState
) {
    val strings = strings()
    val focusManager = LocalFocusManager.current
    Column(
        modifier = modifier
            .fillMaxSize()
            .pointerInput(Unit) {
                detectTapGestures(onTap = { focusManager.clearFocus() })
            }
            .verticalScroll(scrollState)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        EventSectionCard(title = strings.accountDetails) {
            ProfileAvatarSection(
                avatarUrl = resolveMediaUrl(
                    uiState.editableUser.avatarDataUrl
                        ?: uiState.editableUser.avatarUrl
                        ?: uiState.user?.avatarUrl
                ),
                isEditing = uiState.isEditing,
                onPickAvatar = onPickAvatar,
                strings = strings
            )
            ProfileEditableField(
                label = strings.username,
                value = uiState.editableUser.username,
                enabled = uiState.isEditing && !isDeleting,
                onValueChange = { updated ->
                    onFieldChange { it.copy(username = updated) }
                }
            )
            ProfileEditableField(
                label = strings.firstName,
                value = uiState.editableUser.firstName,
                enabled = uiState.isEditing && !isDeleting,
                onValueChange = { updated ->
                    onFieldChange { it.copy(firstName = updated) }
                }
            )
            ProfileEditableField(
                label = strings.lastName,
                value = uiState.editableUser.lastName,
                enabled = uiState.isEditing && !isDeleting,
                onValueChange = { updated ->
                    onFieldChange { it.copy(lastName = updated) }
                }
            )
            ProfileEditableField(
                label = strings.phone,
                value = uiState.editableUser.phone,
                enabled = uiState.isEditing && !isDeleting,
                onValueChange = { updated ->
                    onFieldChange { it.copy(phone = updated) }
                }
            )
            ProfileStaticField(
                label = strings.email,
                value = uiState.user?.email.orEmpty()
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                if (uiState.isEditing) {
                    EventTextAction(
                        text = strings.cancel,
                        onClick = onCancelEditing,
                        enabled = !uiState.isSaving && !isDeleting
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    EventPrimaryButton(
                        text = strings.save,
                        onClick = onSave,
                        enabled = !uiState.isSaving && !isDeleting,
                        isLoading = uiState.isSaving
                    )
                } else {
                    EventOutlineButton(
                        text = strings.edit,
                        onClick = onStartEditing,
                        enabled = !isDeleting
                    )
                }
            }
        }

        EventSectionCard(title = strings.interfaceLanguage) {
            LanguageSelector(
                availableLanguages = availableLanguages,
                selectedLanguage = selectedLanguage,
                onLanguageSelected = onLanguageSelected,
                enabled = uiState.isEditing && !isDeleting,
                label = strings.changeLanguage
            )
        }

        AiConsentSection(strings = strings)

        EventSectionCard(title = strings.logout) {
            Text(
                text = strings.profileLogoutDescription,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            EventPrimaryButton(
                text = strings.logout,
                onClick = onLogoutClick,
                enabled = !isLoggingOut && !isDeleting,
                isLoading = isLoggingOut,
                modifier = Modifier.fillMaxWidth()
            )
        }

        EventSectionCard(title = strings.deleteAccountTitle) {
            Text(
                text = strings.deleteAccountDescription,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            EventDangerButton(
                text = strings.deleteAccountAction,
                onClick = onDeleteAccountClick,
                enabled = !isDeleting,
                isLoading = isDeleting,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun LanguageSelector(
    availableLanguages: List<AppLanguage>,
    selectedLanguage: AppLanguage,
    onLanguageSelected: (AppLanguage) -> Unit,
    enabled: Boolean,
    label: String
) {
    var expanded by remember { mutableStateOf(false) }
    val isWeb = getPlatform().isWeb

    // На Web используем englishName (латиница), на мобильных - nativeName
    fun AppLanguage.displayName(): String = if (isWeb) englishName else nativeName

    LaunchedEffect(enabled) {
        if (!enabled) {
            expanded = false
        }
    }
    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = {
            if (enabled) {
                expanded = !expanded
            }
        }
    ) {
        OutlinedTextField(
            value = selectedLanguage.displayName(),
            onValueChange = {},
            readOnly = true,
            label = { Text(label) },
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            enabled = enabled,
            colors = eventOutlinedFieldColors(),
            shape = RoundedCornerShape(EventCardInputCorner),
            modifier = Modifier
                .menuAnchor()
                .fillMaxWidth()
        )
        ExposedDropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false }
        ) {
            availableLanguages.forEach { language ->
                val isSelected = language == selectedLanguage
                EventSelectableCard(
                    selected = isSelected,
                    onClick = {
                        onLanguageSelected(language)
                        expanded = false
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 4.dp),
                    contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp)
                ) {
                    Text(
                        text = language.displayName(),
                        style = MaterialTheme.typography.bodyMedium,
                        color = if (isSelected) {
                            EventCardAccentDark
                        } else {
                            MaterialTheme.colorScheme.onSurface
                        }
                    )
                    Spacer(modifier = Modifier.weight(1f))
                    if (isSelected) {
                        Icon(
                            imageVector = Icons.Default.Check,
                            contentDescription = null,
                            tint = EventCardAccentDark,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AiConsentSection(strings: com.ijoin.i18n.Strings) {
    val scope = rememberCoroutineScope()
    val legalRepository = remember { LegalRepository() }
    var aiConsentGranted by remember { mutableStateOf<Boolean?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    var showWithdrawDialog by remember { mutableStateOf(false) }
    var showAiConsentDialog by remember { mutableStateOf(false) }
    var snackbarMessage by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        legalRepository.hasAcceptedAiConsent()
            .onSuccess { aiConsentGranted = it }
            .onFailure { aiConsentGranted = false }
    }

    // Don't show section while loading initial state
    if (aiConsentGranted == null) return

    EventSectionCard(title = strings.aiConsentSectionTitle) {
        Text(
            text = if (aiConsentGranted == true) strings.aiConsentEnabled else strings.aiConsentDisabled,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )

        if (snackbarMessage != null) {
            Text(
                text = snackbarMessage.orEmpty(),
                style = MaterialTheme.typography.bodySmall,
                color = EventCardAccentDark
            )
        }

        if (aiConsentGranted == true) {
            EventOutlineButton(
                text = strings.aiConsentWithdrawButton,
                onClick = { showWithdrawDialog = true },
                enabled = !isLoading,
                modifier = Modifier.fillMaxWidth()
            )
        } else {
            EventPrimaryButton(
                text = strings.aiConsentEnableButton,
                onClick = { showAiConsentDialog = true },
                enabled = !isLoading,
                isLoading = isLoading,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }

    if (showWithdrawDialog) {
        AlertDialog(
            onDismissRequest = { showWithdrawDialog = false },
            title = { Text(strings.aiConsentWithdrawConfirmTitle) },
            text = { Text(strings.aiConsentWithdrawConfirmMessage) },
            confirmButton = {
                EventPrimaryButton(
                    text = strings.aiConsentWithdrawButton,
                    onClick = {
                        showWithdrawDialog = false
                        isLoading = true
                        scope.launch {
                            legalRepository.withdrawAiConsent()
                                .onSuccess {
                                    aiConsentGranted = false
                                    snackbarMessage = strings.aiConsentWithdrawnSuccess
                                }
                                .onFailure {
                                    snackbarMessage = it.message
                                }
                            isLoading = false
                        }
                    },
                    isLoading = isLoading
                )
            },
            dismissButton = {
                EventOutlineButton(
                    text = strings.cancel,
                    onClick = { showWithdrawDialog = false }
                )
            }
        )
    }

    if (showAiConsentDialog) {
        AiConsentDialog(
            onAccepted = {
                aiConsentGranted = true
                showAiConsentDialog = false
                snackbarMessage = strings.aiConsentEnabledSuccess
            },
            onDeclined = {
                showAiConsentDialog = false
            },
            legalRepository = legalRepository
        )
    }
}

@Composable
private fun ProfileAvatarSection(
    avatarUrl: String?,
    isEditing: Boolean,
    onPickAvatar: () -> Unit,
    strings: com.ijoin.i18n.Strings
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = "${strings.profile} photo",
            style = MaterialTheme.typography.bodyMedium,
            color = EventCardAccentDark
        )
        Box(
            modifier = Modifier
                .size(96.dp)
                .clip(CircleShape)
                .background(EventCardAccentSoft),
            contentAlignment = Alignment.Center
        ) {
            if (!avatarUrl.isNullOrBlank()) {
                AsyncImage(
                    model = avatarUrl,
                    contentDescription = null,
                    modifier = Modifier.fillMaxSize()
                )
            } else {
                Icon(
                    imageVector = Icons.Filled.Person,
                    contentDescription = null,
                    tint = EventCardAccentDark
                )
            }
        }
        if (isEditing) {
            EventOutlineButton(
                text = strings.addMedia,
                onClick = onPickAvatar,
                leadingIcon = Icons.Filled.Image
            )
        }
    }
}

@Composable
private fun ProfileEditableField(
    label: String,
    value: String,
    enabled: Boolean,
    onValueChange: (String) -> Unit
) {
    if (enabled) {
        OutlinedTextField(
            modifier = Modifier.fillMaxWidth(),
            value = value,
            onValueChange = onValueChange,
            label = { Text(label) },
            colors = eventOutlinedFieldColors(),
            singleLine = true
        )
    } else {
        ProfileStaticField(label = label, value = value.ifBlank { "—" })
    }
}

@Composable
private fun ProfileStaticField(
    label: String,
    value: String
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyLarge,
            color = EventCardAccentDark
        )
    }
}
